// Explore-stats page: Top askers and responders (#207).
// Dual ranked tables (askers, responders), most-questioned card, asker ×
// responder co-occurrence matrix, and per-portfolio answered-question count.
// Names link to /parliamentarians/:phid via the backend's best-effort
// surname-resolution. Party-aware co-occurrence (Dixer detection) waits on
// #196 — note the placeholder caveat on the page.

import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper, Typography, Grid, Box, Card, CardContent, Stack, Link,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatBar, PALETTE } from './charts';

// Render a name as a router link when we have a PHID; plain text otherwise.
function NameCell({ name, phid }) {
  if (phid) {
    return (
      <Link component={RouterLink} to={`/parliamentarians/${phid}`}
            underline="hover">
        {name}
      </Link>
    );
  }
  return <>{name}</>;
}

// Simple co-occurrence heatmap — askers as rows, responders as columns,
// cell intensity ∝ Q count. Plain SVG so it scales with the grid layout.
function Heatmap({ askers, responders, cells }) {
  if (!askers.length || !responders.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Not enough data yet to draw a co-occurrence matrix.
      </Typography>
    );
  }
  const byKey = {};
  let max = 0;
  cells.forEach((c) => {
    byKey[`${c.a}|${c.r}`] = c.count;
    if (c.count > max) max = c.count;
  });
  const colW = 110;
  const rowH = 28;
  const labelW = 130;
  const width = labelW + responders.length * colW + 20;
  const height = 40 + askers.length * rowH;
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <svg width={width} height={height}
           style={{ fontFamily: 'inherit', fontSize: 12 }}>
        {responders.map((r, i) => (
          <text key={r} x={labelW + i * colW + colW / 2} y={20}
                textAnchor="middle">
            {r}
          </text>
        ))}
        {askers.map((a, i) => (
          <g key={a}>
            <text x={labelW - 6} y={40 + i * rowH + 18}
                  textAnchor="end">
              {a}
            </text>
            {responders.map((r, j) => {
              const v = byKey[`${a}|${r}`] || 0;
              const opacity = max ? v / max : 0;
              return (
                <g key={r}>
                  <rect x={labelW + j * colW}
                        y={40 + i * rowH}
                        width={colW - 4} height={rowH - 4}
                        fill={PALETTE[1]} fillOpacity={opacity}
                        stroke="#ccc" strokeWidth={0.5}>
                    <title>{`${a} → ${r}: ${v}`}</title>
                  </rect>
                  {v > 0 && (
                    <text x={labelW + j * colW + colW / 2 - 2}
                          y={40 + i * rowH + 18}
                          textAnchor="middle"
                          fill={opacity > 0.5 ? '#fff' : '#000'}>
                      {v}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        ))}
      </svg>
    </Box>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">{label}</Typography>
        <Typography variant="h4" sx={{ mt: 0.5, lineHeight: 1.1 }}>
          {value ?? '—'}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary">
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function QtPeople() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getExploreStat('qt-people');
        if (active) setData(res);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const c = data?.charts || {};
  const askers = c.top_askers || [];
  const responders = c.top_responders || [];
  const co = c.cooccurrence || { askers: [], responders: [], cells: [] };
  const mq = c.most_questioned;

  // Prep simple bar-chart data for portfolio rollup.
  const portfolioBarData = useMemo(
    () => (c.per_portfolio || []).slice(0, 12),
    [c.per_portfolio],
  );

  return (
    <ExploreStatsPage
      title="Top askers and responders"
      description="Who asks the questions, and who answers them. Ranked from every captured Question Time question in the `house_questions` index. Names link to the parliamentarian directory where the surname could be unambiguously resolved."
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="qt-people"
    >
      {data && (
        <Box>
          {mq && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <StatCard label="Most-questioned MP"
                          value={mq.name}
                          sub={`${mq.count} question${mq.count === 1 ? '' : 's'}${
                            mq.portfolio ? ' — ' + mq.portfolio : ''}`} />
              </Grid>
              <Grid item xs={6} md={4}>
                <StatCard label="Distinct askers"
                          value={askers.length}
                          sub="captured" />
              </Grid>
              <Grid item xs={6} md={4}>
                <StatCard label="Distinct responders"
                          value={responders.length}
                          sub="captured" />
              </Grid>
            </Grid>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Top askers</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell align="right">Questions asked</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {askers.map((a) => (
                      <TableRow key={a.name}>
                        <TableCell>
                          <NameCell name={a.name} phid={a.phid} />
                        </TableCell>
                        <TableCell align="right">{a.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Top responders</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Portfolio</TableCell>
                      <TableCell align="right">Answers</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {responders.map((r) => (
                      <TableRow key={r.name}>
                        <TableCell>
                          <NameCell name={r.name} phid={r.phid} />
                        </TableCell>
                        <TableCell>{r.portfolio || ''}</TableCell>
                        <TableCell align="right">{r.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Asker × responder co-occurrence
                </Typography>
                <Typography variant="caption" color="text.secondary"
                            sx={{ display: 'block', mb: 1 }}>
                  Top 8 of each axis. Once asker party/electorate enrichment
                  lands (#196), this matrix gains a Dixer-detection slice.
                </Typography>
                <Heatmap
                  askers={co.askers || []}
                  responders={co.responders || []}
                  cells={co.cells || []}
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Answered questions by portfolio
                </Typography>
                <StatBar data={portfolioBarData} xKey="portfolio"
                         yKey="count" layout="vertical" height={360} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Top askers</Typography>
                <StatBar data={askers.slice(0, 10).map((a) => ({
                  name: a.name, count: a.count,
                }))} xKey="name" yKey="count" layout="vertical" height={360} />
              </Paper>
            </Grid>
          </Grid>

          <Typography variant="caption" color="text.secondary">
            Browse the underlying transcripts on the{' '}
            <RouterLink to="/house-questions">
              House Questions (QT)
            </RouterLink>{' '}
            search page.
          </Typography>
        </Box>
      )}
    </ExploreStatsPage>
  );
}
